// CustomerDashboard.js - Improved Performance and Functionality

import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, fetchCsrfToken } from './authFetch';
import { UserContext } from './App';
import { 
  Search, Users, TrendingUp, Clock, Filter, Star, MapPin, 
  Calendar, AlertTriangle, DollarSign, FileText, BarChart3,
  ChevronRight, Bell, Plus, Upload, Download, UserCheck,
  Activity, Target, Zap, RefreshCw
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CustomerDashboard() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const navigate = useNavigate();
  
  // State
  const [dashboardData, setDashboardData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentData, setSegmentData] = useState(null);
  const [loadingSegment, setLoadingSegment] = useState(false);
  
  // New customer form state - now includes ALL address fields
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    county: ''
  });

  // Check if user is admin
  const isAdmin = currentUser?.is_staff || (currentUser?.groups || []).some(g => (g.name || '').toLowerCase() === 'admin');

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authFetch(`${BASE_URL}/api/dashboard/summary/`);
      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search functionality with debouncing
  const performSearch = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await authFetch(`${BASE_URL}/api/customers/search/?q=${encodeURIComponent(query.trim())}&limit=10`);
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      } else {
        console.error('Search failed:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, performSearch]);

  // Fetch segment data
  const fetchSegmentData = useCallback(async (segmentKey) => {
    try {
      setLoadingSegment(true);
      setSelectedSegment(segmentKey);
      
      const response = await authFetch(`${BASE_URL}/api/customers/segments/?segment=${segmentKey}&page=1`);
      
      if (response.ok) {
        const data = await response.json();
        setSegmentData(data);
      } else {
        console.error('Failed to fetch segment data:', response.status);
        setSegmentData(null);
      }
    } catch (error) {
      console.error('Error fetching segment data:', error);
      setSegmentData(null);
    } finally {
      setLoadingSegment(false);
    }
  }, []);

  // Handle customer selection from search
  const handleCustomerSelect = (customer) => {
    navigate(`/customers/${customer.id}`);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle adding new customer
  const handleAddCustomer = async () => {
    const { first_name, last_name } = newCustomer;
    if (!first_name || !last_name) {
      alert('First and last name are required.');
      return;
    }

    try {
      // CRITICAL FIX: Ensure we have a fresh CSRF token before POST request
      await fetchCsrfToken();
      
      const response = await authFetch(`${BASE_URL}/api/customers/`, {
        method: 'POST',
        body: JSON.stringify(newCustomer),
      });

      if (!response.ok) {
        throw new Error('Failed to create customer');
      }

      const createdCustomer = await response.json();
      
      // Reset form
      setNewCustomer({ 
        first_name: '', 
        last_name: '', 
        email: '', 
        phone: '', 
        street_address: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        county: ''
      });
      setShowNewCustomerForm(false);
      
      // Refresh dashboard data
      fetchDashboardData();
      
      // Navigate to the new customer's profile
      navigate(`/customers/${createdCustomer.id}`);
    } catch (error) {
      console.error("Error creating customer:", error);
      setError(error.message);
    }
  };

  // Navigate to customer list with filters
  const navigateToCustomerList = (filter = '') => {
    if (filter) {
      navigate(`/customers?filter=${filter}`);
    } else {
      navigate('/customers');
    }
  };

  // Initial data load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <div className="text-lg text-gray-600">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const segments = dashboardData?.segments || [];
  const recentActivity = dashboardData?.recent_activity || [];
  const urgentTasks = dashboardData?.urgent_tasks || [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Customer Dashboard</h1>
          <p className="text-gray-600">Manage and analyze your customer relationships</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <div className="col-span-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Total Customers</p>
                <p className="text-3xl font-bold">{(stats.total_customers || 0).toLocaleString()}</p>
                <p className="text-blue-100 text-sm">+{stats.new_this_month || 0} this month</p>
              </div>
              <Users className="h-12 w-12 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-green-500" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Growth Rate</p>
                <p className="text-xl font-bold">{stats.growth_rate || 0}%</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Star className="h-8 w-8 text-yellow-500" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Active Policies</p>
                <p className="text-xl font-bold">{(stats.active_policies || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-purple-500" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Recent Activity</p>
                <p className="text-xl font-bold">{stats.recent_activity_count || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-indigo-500" />
              <div className="ml-3">
                <p className="text-sm text-gray-600">Avg per Month</p>
                <p className="text-xl font-bold">{Math.round((stats.total_customers || 0) / 12)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Search */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Quick Customer Search</h2>
                <button
                  onClick={() => setShowNewCustomerForm(true)}
                  className="flex items-center bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Customer
                </button>
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers by name, email, phone, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searching && (
                  <RefreshCw className="absolute right-3 top-3 h-5 w-5 text-gray-400 animate-spin" />
                )}
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-4 max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="p-3 border border-gray-200 rounded cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer.first_name && customer.last_name
                                ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                                : 'No Customer Name Entered'}
                            </p>
                            <p className="text-sm text-gray-600">{customer.email || 'No email provided'}</p>
                            {customer.phone && (
                              <p className="text-sm text-gray-600">{customer.phone}</p>
                            )}
                            {(customer.city || customer.state) && (
                              <p className="text-sm text-gray-500">
                                {[customer.city, customer.state].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-sm text-gray-400">
                            ID: {customer.id}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-gray-600">No customers found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setShowNewCustomerForm(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Add "{searchQuery}" as new customer?
                  </button>
                </div>
              )}
            </div>

            {/* Customer Segments */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Segments</h2>
              <div className="grid grid-cols-2 gap-4">
                {segments.map((segment, index) => (
                  <div 
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors hover:shadow-md"
                    onClick={() => fetchSegmentData(segment.key)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{segment.name}</p>
                        <p className="text-2xl font-bold text-gray-900">{segment.count.toLocaleString()}</p>
                        {segment.change && (
                          <p className={`text-xs ${segment.change.startsWith('+') ? 'text-green-600' : 'text-gray-500'}`}>
                            {segment.change}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Segment Details */}
              {selectedSegment && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {segments.find(s => s.key === selectedSegment)?.name || 'Segment Details'}
                    </h3>
                    <button
                      onClick={() => navigateToCustomerList(selectedSegment)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View All →
                    </button>
                  </div>
                  
                  {loadingSegment ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                  ) : segmentData?.results ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {segmentData.results.slice(0, 5).map((customer) => (
                        <div 
                          key={customer.id}
                          onClick={() => navigate(`/customers/${customer.id}`)}
                          className="p-2 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <p className="font-medium text-sm">
                            {customer.first_name && customer.last_name 
                              ? `${customer.first_name} ${customer.last_name}`.trim()
                              : `Customer #${customer.id}`
                            }
                          </p>
                          <p className="text-xs text-gray-500">{customer.email || customer.phone || 'No contact info'}</p>
                        </div>
                      ))}
                      {segmentData.results.length > 5 && (
                        <p className="text-xs text-gray-500 text-center pt-2">
                          And {segmentData.results.length - 5} more...
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No data available</p>
                  )}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => navigateToCustomerList('all')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <Users className="h-8 w-8 text-blue-500 mb-2" />
                  <span className="text-sm font-medium">All Customers</span>
                </button>
                
                <button
                  onClick={() => setShowNewCustomerForm(true)}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                >
                  <Plus className="h-8 w-8 text-green-500 mb-2" />
                  <span className="text-sm font-medium">Add Customer</span>
                </button>
                
                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin/imports')}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                  >
                    <Upload className="h-8 w-8 text-purple-500 mb-2" />
                    <span className="text-sm font-medium">Import Data</span>
                  </button>
                )}
                
                <button
                  onClick={() => window.open('/api/customers/?format=csv', '_blank')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
                >
                  <Download className="h-8 w-8 text-orange-500 mb-2" />
                  <span className="text-sm font-medium">Export Data</span>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Urgent Tasks */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Bell className="h-5 w-5 text-red-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Urgent Tasks</h2>
              </div>
              
              {urgentTasks.length > 0 ? (
                <div className="space-y-3">
                  {urgentTasks.map((task) => (
                    <div key={task.id} className={`p-3 rounded-lg border-l-4 ${
                      task.priority === 'high' ? 'border-red-500 bg-red-50' :
                      task.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                      'border-blue-500 bg-blue-50'
                    }`}>
                      <p className="font-medium text-sm text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {task.count} {task.count === 1 ? 'item' : 'items'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No urgent tasks</p>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              
              {recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.title}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced New Customer Form Modal with Complete Address Fields */}
        {showNewCustomerForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Customer</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name Fields */}
                  <input
                    type="text"
                    placeholder="First Name *"
                    value={newCustomer.first_name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Last Name *"
                    value={newCustomer.last_name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {/* Contact Fields */}
                  <input
                    type="email"
                    placeholder="Email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div>
                    <input
                      type="tel"
                      placeholder="Phone (e.g., 352-450-1155)"
                      value={newCustomer.phone}
                      onChange={(e) => {
                        // Auto-format phone number with dashes
                        let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length >= 6) {
                          value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                        } else if (value.length >= 3) {
                          value = value.slice(0, 3) + '-' + value.slice(3);
                        }
                        setNewCustomer({ ...newCustomer, phone: value });
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: 352-450-1155</p>
                  </div>
                  
                  {/* Address Fields */}
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={newCustomer.street_address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, street_address: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:col-span-2"
                  />
                  <input
                    type="text"
                    placeholder="Address Line 2 (Optional)"
                    value={newCustomer.address_line2}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address_line2: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:col-span-2"
                  />
                  <input
                    type="text"
                    placeholder="City"
                    value={newCustomer.city}
                    onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="State (e.g., FL)"
                    value={newCustomer.state}
                    onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value.toUpperCase() })}
                    maxLength="2"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div>
                    <input
                      type="text"
                      placeholder="Zip Code (5 digits)"
                      value={newCustomer.zip_code}
                      onChange={(e) => {
                        // Only allow digits and limit to 5 characters
                        const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setNewCustomer({ ...newCustomer, zip_code: value });
                      }}
                      maxLength="5"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">5 digits only (e.g., 32162)</p>
                  </div>
                  <input
                    type="text"
                    placeholder="County"
                    value={newCustomer.county}
                    onChange={(e) => setNewCustomer({ ...newCustomer, county: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowNewCustomerForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCustomer}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDashboard;